import { checkinsApi } from './api'

export interface LocalCheckInState {
  id: string
  locationId: string
  locationName: string
  locationAddress: string
  checkInTime: string
  isWithinGeofence: boolean
  lastSynced: string
  userId: string
}

class CheckInStateManager {
  private readonly STORAGE_KEY = 'current_checkin_state'
  private readonly USER_ID_KEY = 'current_user_id'

  // Store check-in state locally
  storeCheckInState(checkInData: any, userId: string): void {
    try {
      const localState: LocalCheckInState = {
        id: checkInData.id,
        locationId: checkInData.locationId || checkInData.location?.id,
        locationName: checkInData.location?.name || checkInData.locationName,
        locationAddress: checkInData.location?.address || checkInData.locationAddress,
        checkInTime: checkInData.checkInTime,
        isWithinGeofence: checkInData.isWithinGeofence,
        lastSynced: new Date().toISOString(),
        userId
      }

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(localState))
      localStorage.setItem(this.USER_ID_KEY, userId)
      
      console.log('📱 Stored local check-in state:', localState)
    } catch (error) {
      console.error('Error storing check-in state:', error)
    }
  }

  // Get stored check-in state
  getStoredCheckInState(): LocalCheckInState | null {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY)
      if (!stored) return null

      const state = JSON.parse(stored) as LocalCheckInState
      const currentUserId = localStorage.getItem(this.USER_ID_KEY)

      // Only return state if it's for the current user
      if (currentUserId && state.userId === currentUserId) {
        return state
      }

      // Clear stale state for different user
      this.clearCheckInState()
      return null
    } catch (error) {
      console.error('Error reading check-in state:', error)
      return null
    }
  }

  // Clear stored check-in state
  clearCheckInState(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY)
      console.log('📱 Cleared local check-in state')
    } catch (error) {
      console.error('Error clearing check-in state:', error)
    }
  }

  // Update geofence status
  updateGeofenceStatus(isWithinGeofence: boolean): void {
    try {
      const state = this.getStoredCheckInState()
      if (state) {
        state.isWithinGeofence = isWithinGeofence
        state.lastSynced = new Date().toISOString()
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(state))
        console.log('📱 Updated geofence status:', isWithinGeofence)
      }
    } catch (error) {
      console.error('Error updating geofence status:', error)
    }
  }

  // Check if local state is stale (older than 24 hours)
  isStateStale(): boolean {
    try {
      const state = this.getStoredCheckInState()
      if (!state) return true

      const lastSynced = new Date(state.lastSynced)
      const now = new Date()
      const hoursDiff = (now.getTime() - lastSynced.getTime()) / (1000 * 60 * 60)

      return hoursDiff > 24
    } catch (error) {
      console.error('Error checking state staleness:', error)
      return true
    }
  }

  // Sync local state with server state
  async syncWithServer(): Promise<LocalCheckInState | null> {
    try {
      console.log('📱 Syncing with server...')
      
      // Get current state from server
      const serverState = await checkinsApi.getCurrent()
      
      if (serverState) {
        // User is checked in on server - update local state
        const userId = localStorage.getItem(this.USER_ID_KEY)
        if (userId) {
          this.storeCheckInState(serverState, userId)
          console.log('📱 Updated local state with server data')
          return this.getStoredCheckInState()
        }
      } else {
        // User is not checked in on server - clear local state
        console.log('📱 Server reports no active checkin - clearing local state')
        this.clearCheckInState()
      }

      return serverState
    } catch (error) {
      console.error('Error syncing with server:', error)
      // If we can't reach server, return local state if it's not stale
      if (!this.isStateStale()) {
        console.log('📱 Server unreachable, using local state if not stale')
        return this.getStoredCheckInState()
      }
      return null
    }
  }

  // Get current check-in state (server first, then local as fallback)
  async getCurrentCheckInState(): Promise<LocalCheckInState | null> {
    try {
      console.log('📱 Getting current check-in state...')
      
      // First check local state to see if we have something recent
      const localState = this.getStoredCheckInState()
      console.log('📱 Local state found:', localState ? 'yes' : 'no')
      
      // Then try to get from server
      const serverState = await checkinsApi.getCurrent()
      console.log('📱 Server state found:', serverState ? 'yes' : 'no')
      
      if (serverState) {
        // User is checked in on server - update local state
        const userId = localStorage.getItem(this.USER_ID_KEY)
        if (userId) {
          this.storeCheckInState(serverState, userId)
          console.log('📱 Server has active checkin - updated local state')
        }
        return this.getStoredCheckInState()
      } else {
        // User is not checked in on server - but check if local state is recent
        console.log('📱 Server reports no active checkin')
        
        if (localState && !this.isStateStale()) {
          console.log('📱 Using local state as fallback (not stale)')
          return localState
        } else {
          console.log('📱 Clearing local state - server says no checkin and local is stale/missing')
        this.clearCheckInState()
        return null
        }
      }
    } catch (error) {
      console.error('Error getting current check-in state:', error)
      
      // If server is unreachable, try local state only if it's not stale
      const localState = this.getStoredCheckInState()
      if (localState && !this.isStateStale()) {
        console.log('📱 Server unreachable, using local state (not stale)')
        return localState
      } else {
        console.log('📱 Server unreachable, local state is stale or missing - clearing')
        this.clearCheckInState()
        return null
      }
    }
  }

  // Handle user logout
  handleLogout(): void {
    this.clearCheckInState()
    localStorage.removeItem(this.USER_ID_KEY)
  }

  // Handle user login
  handleLogin(userId: string): void {
    localStorage.setItem(this.USER_ID_KEY, userId)
  }

  // Force clear all state and restart (for debugging)
  forceClearAndRestart(): void {
    try {
      console.log('📱 Force clearing all state and restarting...')
      this.clearCheckInState()
      localStorage.removeItem(this.USER_ID_KEY)
      sessionStorage.clear()
      
      // Force a hard refresh
      window.location.reload()
    } catch (error) {
      console.error('Error force clearing state:', error)
    }
  }
}

// Create singleton instance
export const checkInStateManager = new CheckInStateManager() 